import { useState, useEffect, useCallback, createContext, useContext } from "react";
import Confetti from "react-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Star, Trophy, Flame, Sparkles, Crown, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fadeIn, fadeInUp, scaleIn } from "@/lib/motion-variants";
import { CelebrationBody } from "@/components/celebrations/CelebrationBody";

// Celebration Context
const CelebrationContext = createContext(null);

export function useCelebration() {
  const context = useContext(CelebrationContext);
  if (!context) {
    throw new Error("useCelebration must be used within a CelebrationProvider");
  }
  return context;
}

// Celebration types
const CELEBRATION_TYPES = {
  badge: {
    title: "Badge Unlocked!",
    icon: Trophy,
    gradient: "from-amber-500 via-yellow-400 to-orange-500",
    particleColors: ["#fbbf24", "#f59e0b", "#d97706", "#ffffff"],
  },
  levelUp: {
    title: "Level Up!",
    icon: Crown,
    gradient: "from-violet-600 via-purple-500 to-indigo-600",
    particleColors: ["#8b5cf6", "#a855f7", "#7c3aed", "#ffffff"],
  },
  streak: {
    title: "Streak Milestone!",
    icon: Flame,
    gradient: "from-orange-500 via-red-500 to-rose-500",
    particleColors: ["#f97316", "#ef4444", "#f43f5e", "#ffffff"],
  },
  achievement: {
    title: "Achievement Unlocked!",
    icon: Star,
    gradient: "from-emerald-500 via-green-400 to-teal-500",
    particleColors: ["#10b981", "#22c55e", "#14b8a6", "#ffffff"],
  },
  xp: {
    title: "XP Bonus!",
    icon: Sparkles,
    gradient: "from-blue-500 via-cyan-400 to-sky-500",
    particleColors: ["#3b82f6", "#06b6d4", "#0ea5e9", "#ffffff"],
  },
};

// Provider Component
export function CelebrationProvider({ children }) {
  const [celebration, setCelebration] = useState(null);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const celebrate = useCallback((type, data = {}) => {
    setCelebration({ type, data, timestamp: Date.now() });
  }, []);

  const celebrateBadge = useCallback((badge) => {
    celebrate("badge", { badge });
  }, [celebrate]);

  const celebrateLevelUp = useCallback((newLevel) => {
    celebrate("levelUp", { level: newLevel });
  }, [celebrate]);

  const celebrateStreak = useCallback((streakDays) => {
    celebrate("streak", { days: streakDays });
  }, [celebrate]);

  const celebrateXP = useCallback((xpAmount) => {
    celebrate("xp", { amount: xpAmount });
  }, [celebrate]);

  const dismiss = useCallback(() => {
    setCelebration(null);
  }, []);

  // Process gamification response and trigger celebrations
  const processGamificationResponse = useCallback((response, previousLevel = null) => {
    if (!response) return;

    // Check for new badges
    if (response.new_badges && response.new_badges.length > 0) {
      // Show first badge immediately, queue others
      response.new_badges.forEach((badge, index) => {
        setTimeout(() => {
          celebrateBadge(badge);
        }, index * 3500); // Stagger celebrations
      });
    }

    // Check for level up (if level info provided)
    if (previousLevel && response.level && response.level.level > previousLevel.level) {
      setTimeout(() => {
        celebrateLevelUp(response.level);
      }, response.new_badges ? response.new_badges.length * 3500 : 0);
    }
  }, [celebrateBadge, celebrateLevelUp]);

  return (
    <CelebrationContext.Provider
      value={{
        celebrate,
        celebrateBadge,
        celebrateLevelUp,
        celebrateStreak,
        celebrateXP,
        processGamificationResponse,
        dismiss,
      }}
    >
      {children}
      <CelebrationOverlay
        celebration={celebration}
        windowSize={windowSize}
        onDismiss={dismiss}
      />
    </CelebrationContext.Provider>
  );
}

// Celebration Overlay Component
function CelebrationOverlay({ celebration, windowSize, onDismiss }) {
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (celebration) {
      setShowConfetti(true);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        onDismiss();
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowConfetti(false);
    }
  }, [celebration, onDismiss]);

  if (!celebration) return null;

  const config = CELEBRATION_TYPES[celebration.type] || CELEBRATION_TYPES.achievement;
  const IconComponent = config.icon;

  return (
    <AnimatePresence>
      {celebration && (
        <>
          {/* Confetti */}
          {showConfetti && (
            <Confetti
              width={windowSize.width}
              height={windowSize.height}
              recycle={false}
              numberOfPieces={300}
              gravity={0.2}
              colors={config.particleColors}
              style={{ position: "fixed", top: 0, left: 0, zIndex: 9998 }}
            />
          )}

          {/* Overlay */}
          <motion.div
            {...fadeIn} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
            onClick={onDismiss}
            data-testid="celebration-overlay"
          >
            <motion.div
              initial={{ scale: 0, rotate: -10 }}
              animate={{ 
                scale: 1, 
                rotate: 0,
                transition: { type: "spring", stiffness: 200, damping: 15 }
              }}
              exit={{ scale: 0, rotate: 10, opacity: 0 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Glow effect */}
              <div className={`absolute inset-0 bg-gradient-to-r ${config.gradient} rounded-3xl blur-2xl opacity-50 animate-pulse`} />
              
              {/* Main card */}
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 text-center max-w-sm border border-white/10 shadow-2xl">
                {/* Floating particles */}
                <motion.div
                  className="absolute -top-4 -left-4"
                  animate={{ y: [-5, 5, -5], rotate: [0, 15, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-8 h-8 text-yellow-400" />
                </motion.div>
                <motion.div
                  className="absolute -top-2 -right-6"
                  animate={{ y: [5, -5, 5], rotate: [0, -15, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                >
                  <PartyPopper className="w-6 h-6 text-pink-400" />
                </motion.div>
                <motion.div
                  className="absolute -bottom-3 -right-3"
                  animate={{ y: [-3, 3, -3] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                </motion.div>

                {/* Icon */}
                <motion.div
                  {...scaleIn} 
                  transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                  className={`w-24 h-24 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}
                >
                  {celebration.type === "badge" && celebration.data.badge ? (
                    <span className="text-5xl">{celebration.data.badge.icon}</span>
                  ) : (
                    <IconComponent className="w-12 h-12 text-white" />
                  )}
                </motion.div>

                {/* Title */}
                <motion.h2
                  {...fadeInUp} 
                  transition={{ delay: 0.3 }}
                  className={`text-2xl font-bold mb-2 bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}
                >
                  {config.title}
                </motion.h2>

                {/* Content based on type — extracted to keep this file flat */}
                <CelebrationBody type={celebration.type} data={celebration.data} />

                {/* Dismiss button */}
                <motion.div
                  {...fadeIn} 
                  transition={{ delay: 0.6 }}
                  className="mt-6"
                >
                  <Button
                    onClick={onDismiss}
                    className={`w-full bg-gradient-to-r ${config.gradient} text-white font-semibold py-2 rounded-xl hover:opacity-90 transition-opacity`}
                    data-testid="celebration-dismiss"
                  >
                    {t("common.close") || "Awesome!"}
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default CelebrationProvider;
